import { useCommandManager } from '../state/commandStore';
import { normalizeShortcutString } from './revitShortcuts';

/**
 * SketchUp keyboard shortcuts import
 *
 * Parses a SketchUp preferences file exported from Window > Preferences >
 * Shortcuts > Export (plain text, commonly "preferences.dat") and applies
 * each binding to the equivalent app command — the Sketch workbench tools
 * in particular — so muscle memory from SketchUp, including the user's own
 * customizations, keeps working here.
 *
 * SketchUp exports have shipped a few line shapes over versions/locales, so
 * the parser stays tolerant. Recognized shapes (path and key on either side
 * when unambiguous):
 * ```
 * Tools > Line⇥L          (tab-separated)
 * Tools/Line = L          ('=' separated)
 * [Tools > Line]  L       (bracketed path, whitespace separated)
 * ```
 * The key side is a single key optionally preceded by modifiers ("Ctrl+N",
 * "Shift+Z", "L", "Space") — SketchUp has no multi-key sequences. Command
 * resolution uses the path's LAST segment ("Tools > Sandbox > From Contours"
 * → "from contours"), which is unique across the SketchUp menus.
 *
 * Sketch-scoped targets (see core/sketchToolCommands.ts) only take effect
 * inside the Sketch workbench; shared commands (Undo, File, ...) behave
 * like their Revit-import counterparts.
 */

/** One parsed binding from a SketchUp preferences export. */
export interface SketchupShortcutEntry {
    /** Normalized app-format shortcut string ("L", "Ctrl+N", "Space"). */
    shortcut: string;
    /** Normalized lowercase menu path ("tools > line"). */
    commandPath: string;
}

/** One binding successfully assigned to an app command. */
export interface SketchupImportApplied {
    commandId: string;
    commandLabel: string;
    shortcut: string;
    commandPath: string;
    /** Effective shortcut of the command before the import (null if none). */
    replaced: string | null;
}

export type SketchupImportSkipReason =
    /** SketchUp command has no equivalent app command. */
    | 'unmapped-command'
    /** Shortcut already claimed by an earlier entry in the same file. */
    | 'duplicate-shortcut'
    /** Command already received a (different) shortcut from the same file. */
    | 'duplicate-command'
    /** Malformed entry: a key without a recognizable command path. */
    | 'unknown-command';

export interface SketchupImportSkipped {
    shortcut: string;
    commandPath: string;
    reason: SketchupImportSkipReason;
}

/** A command outside the import whose effective shortcut equals an imported one. */
export interface SketchupImportCollision {
    commandId: string;
    commandLabel: string;
    shortcut: string;
}

/** Parse result: the usable bindings plus unrecognizable line count. */
export interface SketchupParseResult {
    entries: SketchupShortcutEntry[];
    /** Non-empty lines that produced no (path, key) pair. */
    unparsedLines: number;
}

export interface SketchupImportSummary {
    /** Number of bindings parsed from the file. */
    total: number;
    applied: SketchupImportApplied[];
    skipped: SketchupImportSkipped[];
    collisions: SketchupImportCollision[];
    /** Non-empty lines that produced no (path, key) pair. */
    unparsedLines: number;
}

export class SketchupShortcutsParseError extends Error {}

/** Modifier combination ending in a single key ("Ctrl+N", "Ctrl+Shift+S"). */
const MODIFIER_COMBO = /^(?:(?:Ctrl|Alt|Shift|Meta|Cmd|Win|Windows|Command)\+)+[A-Za-z0-9]$/i;
/** Plain single character ("L", "3"). */
const SINGLE_KEY = /^[A-Za-z0-9]$/;
/** Named keys SketchUp exports spell out. */
const NAMED_KEY =
    /^(?:F\d{1,2}|Esc(?:ape)?|Del(?:ete)?|Backspace|Enter|Return|Space(?:bar)?|Tab|Home|End|PageUp|PageDown|PgUp|PgDn|Insert|Left|Right|Up|Down)$/i;

const isKeySide = (value: string): boolean =>
    MODIFIER_COMBO.test(value) || SINGLE_KEY.test(value) || NAMED_KEY.test(value);

const isPathSide = (value: string): boolean => /[\\/>]/.test(value);

/** Normalize a SketchUp menu path ("Tools\\Line", "[Camera > Zoom]…"). */
const normalizeCommandPath = (raw: string): string =>
    raw
        .trim()
        .replace(/^\[(.*)\]$/, '$1')
        .replace(/\\|\//g, ' > ')
        .replace(/\.{2,}\s*$/, '')
        .replace(/\s*>\s*/g, ' > ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

/**
 * Normalize a SketchUp key string into the app's format ("Space" stays
 * "Space"; revitShortcuts' normalizer handles modifiers/named keys).
 */
const normalizeKey = (raw: string): string => {
    const normalized = normalizeShortcutString(raw);
    if (normalized.includes('+')) return normalized.replace(/SPACE$/i, 'Space');
    return /^SPACE(BAR)?$/i.test(normalized) ? 'Space' : normalized;
};

/** Decide which side of a split line is the key, the other the menu path. */
const pickSides = (left: string, right: string): { path: string; key: string } | undefined => {
    const leftIsKey = isKeySide(left);
    const rightIsKey = isKeySide(right);
    if (leftIsKey && !rightIsKey) return { path: right, key: left };
    if (rightIsKey && !leftIsKey) return { path: left, key: right };
    if (leftIsKey && rightIsKey) {
        // Both key-shaped: the side carrying a menu separator is the path;
        // with neither, the longer text is.
        if (isPathSide(right)) return { path: right, key: left };
        if (isPathSide(left)) return { path: left, key: right };
        return left.length <= right.length
            ? { path: right, key: left }
            : { path: left, key: right };
    }
    return undefined;
};

/**
 * SketchUp menu path's last segment → app command id candidates (first
 * registered candidate wins). Keys are lowercase; several spellings are
 * listed where SketchUp versions differ ("Push/Pull").
 */
const SKETCHUP_MENU_MAP: Record<string, string[]> = {
    // --- Tools ---
    select: ['sketchSelect'],
    eraser: ['sketchErase'],
    'paint bucket': ['sketchPaintBucket'],
    move: ['sketchMove'],
    rotate: ['sketchRotate'],
    scale: ['sketchScale'],
    'push/pull': ['sketchPushPull'],
    pushpull: ['sketchPushPull'],
    'follow me': ['sketchFollowMe'],
    offset: ['sketchOffset'],
    'tape measure': ['sketchTapeMeasure'],
    dimension: ['sketchDimension'],
    protractor: ['sketchProtractor'],
    text: ['sketchText'],
    '3d text': ['sketchText3D'],
    axes: ['sketchAxes'],
    'section plane': ['sketchSectionPlane'],
    // --- Draw ---
    line: ['sketchLine'],
    arc: ['sketchArc'],
    '2 point arc': ['sketchArc'],
    '3 point arc': ['sketchArc3Point'],
    pie: ['sketchPie'],
    freehand: ['sketchFreehand'],
    circle: ['sketchCircle'],
    polygon: ['toolPolygon'],
    'rotated rectangle': ['sketchRotatedRectangle'],
    rectangle: ['sketchRectangle'],
    // --- Camera ---
    orbit: ['sketchOrbit'],
    pan: ['sketchPan'],
    zoom: ['sketchZoom'],
    'zoom window': ['sketchZoomWindow'],
    'zoom extents': ['sketchZoomExtents'],
    'position camera': ['sketchPositionCamera'],
    'look around': ['sketchLookAround'],
    walk: ['sketchWalk'],
    // --- Sandbox ---
    'from contours': ['sketchSandboxFromContours'],
    'from scratch': ['sketchSandboxFromScratch'],
    smoove: ['sketchSmoove'],
    stamp: ['sketchStamp'],
    drape: ['sketchDrape'],
    'add detail': ['sketchSandboxAddDetail'],
    // --- Edit (shared commands) ---
    undo: ['undo'],
    redo: ['redo'],
    cut: ['cut'],
    copy: ['copy'],
    paste: ['paste'],
    delete: ['delete'],
    'select all': ['selectAll'],
    hide: ['hideElements'],
    unhide: ['resetTemporaryHideIsolate'],
    // --- File ---
    new: ['newProject'],
    open: ['openProject'],
    save: ['saveProject'],
    'save as': ['saveProjectAs'],
};

/** Resolve the app command ids a menu path may bind to (last segment). */
const resolveSketchupCommandIds = (commandPath: string): string[] => {
    const last = commandPath.split(' > ').pop() ?? '';
    return SKETCHUP_MENU_MAP[last] ?? [];
};

/**
 * Parse a SketchUp preferences export into shortcut entries.
 * Throws {@link SketchupShortcutsParseError} for empty input; unrecognized
 * non-empty lines are tolerated and counted in `unparsedLines`.
 */
export function parseSketchupShortcuts(text: string): SketchupParseResult {
    if (!text || !text.trim()) {
        throw new SketchupShortcutsParseError('The file is empty.');
    }

    const entries: SketchupShortcutEntry[] = [];
    let unparsedLines = 0;

    for (const rawLine of text.split(/\r?\n/)) {
        const line = rawLine.trim();
        if (!line || line.startsWith('#') || line.startsWith(';')) continue;

        let left: string;
        let right: string;
        if (line.includes('\t')) {
            const at = line.indexOf('\t');
            left = line.slice(0, at).trim();
            right = line.slice(at + 1).trim();
        } else if (line.includes('=')) {
            const at = line.indexOf('=');
            left = line.slice(0, at).trim();
            right = line.slice(at + 1).trim();
        } else {
            // Bracketed ("[Tools > Line]  L") or wide-space separated.
            const bracket = /^\[(.+?)\]\s*(.+)$/.exec(line);
            const spaced = /^(.*\S)\s{2,}(\S.*)$/.exec(line);
            const candidate = bracket ?? spaced;
            if (!candidate) {
                unparsedLines++;
                continue;
            }
            left = candidate[1].trim();
            right = candidate[2].trim();
        }

        const sides = pickSides(left, right);
        if (!sides || !sides.key || !sides.path) {
            unparsedLines++;
            continue;
        }
        const shortcut = normalizeKey(sides.key);
        const commandPath = normalizeCommandPath(sides.path);
        if (!shortcut || !commandPath) {
            unparsedLines++;
            continue;
        }
        entries.push({ shortcut, commandPath });
    }

    return { entries, unparsedLines };
}

/**
 * Import a SketchUp preferences export: parse → resolve → apply as user
 * shortcuts on the command store (persisted by zustand persist).
 *
 * Rules: file order wins — the first binding for a shortcut (or for a
 * command) is applied and later duplicates are reported as skipped.
 * Assignments are still applied when they shadow a command outside the
 * import; those pairs are returned as `collisions` for the caller to
 * surface.
 */
export function importSketchupShortcuts(text: string): SketchupImportSummary {
    const { entries, unparsedLines } = parseSketchupShortcuts(text);
    const { commands, updateShortcut } = useCommandManager.getState();

    const applied: SketchupImportApplied[] = [];
    const skipped: SketchupImportSkipped[] = [];
    const assigned = new Map<string, string>(); // commandId → shortcut
    const claimedShortcuts = new Set<string>(); // shortcuts applied in this run

    for (const entry of entries) {
        const base = { shortcut: entry.shortcut, commandPath: entry.commandPath };
        if (claimedShortcuts.has(entry.shortcut)) {
            skipped.push({ ...base, reason: 'duplicate-shortcut' });
            continue;
        }
        const commandId = resolveSketchupCommandIds(entry.commandPath).find(
            (id) => commands[id] && !assigned.has(id)
        );
        if (!commandId) {
            skipped.push({ ...base, reason: 'unmapped-command' });
            continue;
        }
        if (assigned.has(commandId)) {
            skipped.push({ ...base, reason: 'duplicate-command' });
            continue;
        }
        const command = commands[commandId];
        const replaced = (command.shortcut ?? command.defaultShortcut ?? '').trim() || null;
        updateShortcut(commandId, entry.shortcut);
        assigned.set(commandId, entry.shortcut);
        claimedShortcuts.add(entry.shortcut);
        applied.push({
            commandId,
            commandLabel: command.label,
            shortcut: entry.shortcut,
            commandPath: entry.commandPath,
            replaced,
        });
    }

    // Effective-shortcut collisions with commands the import did not touch.
    const collisions: SketchupImportCollision[] = [];
    const finalCommands = useCommandManager.getState().commands;
    for (const cmd of Object.values(finalCommands)) {
        if (assigned.has(cmd.id)) continue;
        const effective = (cmd.shortcut ?? cmd.defaultShortcut ?? '').trim().toLowerCase();
        if (!effective) continue;
        for (const [commandId, shortcut] of assigned) {
            if (shortcut.toLowerCase() === effective) {
                collisions.push({ commandId: cmd.id, commandLabel: cmd.label, shortcut });
                break;
            }
        }
    }

    return { total: entries.length, applied, skipped, collisions, unparsedLines };
}
